import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useChannel } from '@/context/ChannelContext'
import { useShowcase } from '@/showcase/useShowcase'
import { ShowcaseApiError } from '@/api/showcase'
import { track, trackError } from '@/lib/analytics'

/**
 * Paywall/activation-экран Витрины — два режима:
 * 1) tariff !== 'profi' → классический paywall с CTA «Перейти на Профи».
 * 2) tariff === 'profi' AND state.status='locked' → «Включить Витрину»
 *    через actions.enable (см. сессия 16.06 — у Виктора тариф Профи, но
 *    Витрина в locked. Юзеру не нужно говорить про тариф — только активация).
 */
export function LockedPage() {
  const navigate = useNavigate()
  const { selectedChannel } = useChannel()
  const { actions } = useShowcase(selectedChannel?.uuid)
  const [enabling, setEnabling] = useState(false)
  const [enableError, setEnableError] = useState<string | null>(null)

  const hasProfi = selectedChannel?.tariff === 'profi'

  async function handleEnable() {
    if (!selectedChannel?.uuid) return
    setEnabling(true)
    setEnableError(null)
    try {
      await actions.enable()
      track('showcase_enabled_from_locked', { channelUUID: selectedChannel.uuid })
    } catch (e) {
      trackError(e, 'showcase-enable-from-locked')
      if (e instanceof ShowcaseApiError && e.status === 402) {
        setEnableError('Не удалось включить — проверьте статус Профи в Биллинге')
      } else {
        setEnableError(e instanceof Error ? e.message : 'Не удалось включить Витрину')
      }
    } finally {
      setEnabling(false)
    }
  }

  return (
    <div className="locked-wrap">
      <div className="locked-card">
        <div className="locked-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span>Доступно на Профи</span>
        </div>

        <h2 className="locked-title">
          Бесплатный трафик в&nbsp;ваш канал — <em>из&nbsp;Яндекса и&nbsp;Google</em>
        </h2>

        <p className="locked-sub">
          Витрина публикует посты канала на портале telegraphyx.ru. Поисковики находят страницы, читатели переходят
          в&nbsp;канал. Без рекламы, без бюджета — просто органический поток.
        </p>

        <div className="locked-proof">
          <div className="proof-item">
            <div className="proof-num">84 000</div>
            <div className="proof-lab">страниц уже в&nbsp;поиске</div>
          </div>
          <div className="proof-sep" />
          <div className="proof-item">
            <div className="proof-num">28 000</div>
            <div className="proof-lab">читателей/мес из&nbsp;поисковиков</div>
          </div>
        </div>

        <ul className="locked-perks">
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>AI-агент сам готовит каждый пост к&nbsp;поиску — без вашего участия</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>Любой пост можно убрать из&nbsp;поиска в&nbsp;один клик</span>
          </li>
          <li>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>Дашборд с&nbsp;метриками: позиции, запросы, лучшие посты по&nbsp;трафику</span>
          </li>
        </ul>

        <div className="locked-actions">
          {hasProfi ? (
            <button className="locked-cta" onClick={handleEnable} disabled={enabling}>
              {enabling ? 'Включаем…' : 'Включить Витрину'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          ) : (
            <button className="locked-cta" onClick={() => navigate('/billing')}>
              Перейти на&nbsp;Профи — 4&nbsp;900&nbsp;₽/мес
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          )}
          <button className="locked-secondary" onClick={() => navigate('/dashboard')}>
            Назад к&nbsp;дашборду
          </button>
        </div>

        {enableError && <div className="locked-error">{enableError}</div>}

        <p className="locked-foot">
          {hasProfi
            ? 'Включается и отключается в один клик.'
            : 'Включается и отключается в один клик. Никаких длительных контрактов.'}
        </p>
      </div>

      <style>{`
        .locked-wrap {
          display: flex;
          justify-content: center;
          padding: 16px 0;
        }
        .locked-card {
          width: 100%;
          max-width: 720px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 20px;
          padding: 36px 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          box-shadow: 0 8px 32px -16px rgba(0,0,0,0.08);
        }
        .locked-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: linear-gradient(135deg, #fff4cc, #fff8e1);
          color: #8b6914;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          border-radius: 999px;
          margin-bottom: 20px;
        }
        .locked-badge svg { width: 12px; height: 12px; }
        .locked-error {
          margin-top: 12px;
          padding: 10px 14px;
          background: rgba(229, 70, 70, 0.1);
          border: 1px solid rgba(229, 70, 70, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
          max-width: 400px;
        }
        .locked-title {
          font-size: 28px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          margin: 0 0 12px;
          line-height: 1.2;
          letter-spacing: -0.02em;
          max-width: 540px;
        }
        .locked-title em {
          font-style: normal;
          background: linear-gradient(135deg, #0066ff, #5a8dff);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .locked-sub {
          font-size: 15px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.55;
          margin: 0 0 28px;
          max-width: 560px;
        }
        .locked-proof {
          display: flex;
          align-items: center;
          gap: 24px;
          padding: 18px 28px;
          background: var(--tg-paper, #fafafd);
          border-radius: 14px;
          margin-bottom: 28px;
        }
        .proof-item { display: flex; flex-direction: column; gap: 2px; }
        .proof-num {
          font-size: 22px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          font-variant-numeric: tabular-nums;
        }
        .proof-lab {
          font-size: 11.5px;
          color: var(--tg-mute, #5b6678);
        }
        .proof-sep {
          width: 1px;
          height: 32px;
          background: var(--tg-line-2, #f0f0f4);
        }
        .locked-perks {
          list-style: none;
          margin: 0 0 28px;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
          align-self: stretch;
          max-width: 480px;
          margin-left: auto;
          margin-right: auto;
        }
        .locked-perks li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 14px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
          text-align: left;
        }
        .locked-perks svg {
          width: 18px;
          height: 18px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .locked-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
          align-items: center;
        }
        .locked-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 26px;
          background: var(--tg-blue, #0066ff);
          color: white;
          font-weight: 700;
          font-size: 15px;
          border: 0;
          border-radius: 12px;
          cursor: pointer;
          font-family: inherit;
          box-shadow: 0 4px 16px -4px rgba(0,102,255,0.35);
          transition: background 150ms;
        }
        .locked-cta:hover { background: var(--tg-blue-press, #0052cc); }
        .locked-cta svg { width: 16px; height: 16px; }
        .locked-secondary {
          background: none;
          border: 0;
          color: var(--tg-mute, #5b6678);
          font-family: inherit;
          font-size: 13px;
          padding: 8px 14px;
          cursor: pointer;
          font-weight: 500;
        }
        .locked-secondary:hover { color: var(--tg-ink, #111); }
        .locked-foot {
          margin: 18px 0 0;
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
        }
        @media (max-width: 600px) {
          .locked-card { padding: 28px 22px; }
          .locked-title { font-size: 22px; }
          .locked-proof {
            flex-direction: column;
            gap: 12px;
            padding: 14px 20px;
          }
          .proof-sep { display: none; }
        }
      `}</style>
    </div>
  )
}
